class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.defaultSearchResults = this.predictiveSearchResults.innerHTML;
    // --- NEW: Cache just the suggestions HTML ---
    const defaultSuggestionsNode = this.predictiveSearchResults.querySelector('[data-default-suggestions]');
    this.defaultSuggestionsHTML = defaultSuggestionsNode ? defaultSuggestionsNode.innerHTML : '';

    const defaultPillsNode = this.predictiveSearchResults.querySelector('[data-default-pills]');
    this.defaultPillsHTML = defaultPillsNode ? defaultPillsNode.innerHTML : '';

    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    this.onViewportChange = this.updateResultsMaxHeight.bind(this);

    // --- NEW: Track the current search type (default to all) ---
    this.currentSearchType = 'product,article,page'; 
    // -----------------------------------------------------------

    this.isScrolling = false;

    this.setupEventListeners();
  }

  get inputDebounceDelay() {
    return 150;
  }

  connectedCallback() {
    window.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onViewportChange);
    if (document.activeElement === this.input) this.onFocus();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.requestId++;
    window.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Handles clicks on ALL pills (default state and no-results state)
    this.addEventListener('mousedown', (event) => {
      const pill = event.target.closest('.predictive-search__pill');
      
      if (pill) {
        // This stops the input from losing focus (blurring)
        event.preventDefault(); 
        
        const searchTerm = pill.dataset.searchTerm;
        this.input.value = searchTerm;
        this.onChange(); // This triggers the search
      }

      // --- NEW: Handle Scope/Facet Buttons inside the dropdown ---
      // Assuming you add buttons with class .search-scope-btn and data-type="product"
      const scopeBtn = event.target.closest('.search-scope-btn');
      if (scopeBtn) {
        event.preventDefault();
        // Update styling to show active state
        this.querySelectorAll('.search-scope-btn').forEach(btn => btn.classList.remove('active'));
        scopeBtn.classList.add('active');

        // Update type and re-fetch
        this.currentSearchType = scopeBtn.dataset.type;
        this.onChange(); 
      }
      // -----------------------------------------------------------

    });

  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    // --- ADD THIS BLOCK ---
    if (this.isSyncing) {
      this.isSyncing = false; // Reset the flag
      super.onChange(); // Still run parent's onChange to toggle the 'X' button
      return; // Stop before running sync logic
    }
    // --- END NEW BLOCK ---
    
    super.onChange();
    const newSearchTerm = this.getQuery();

    // --- START SYNC LOGIC ---
    // Loop through all other predictive-search elements and update their value
    this.allPredictiveSearchInstances.forEach((instance) => {
      if (instance !== this && instance.input && instance.input.value !== newSearchTerm) {
        instance.isSyncing = true;
        instance.input.value = newSearchTerm;

        // Manually dispatch an 'input' event on the other instance.
        // This will trigger its own 'onChange' method, which will
        // call toggleResetButton() and restore the default results.
        instance.input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      }
    });
    // --- END SYNC LOGIC ---

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.requestId++;
      this.removeAttribute('loading');
      // If the field is empty, restore the default content.
      this.predictiveSearchResults.innerHTML = this.defaultSearchResults;
      this.removeAttribute('results');
      
      // Now, open the dropdown to show the default state.
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true); // This clears the *current* instance

      // --- START CORRECT SYNC LOGIC FOR RESET ---
      const newSearchTerm = ''; // We know the new term is empty
      this.allPredictiveSearchInstances.forEach((instance) => {
        if (instance !== this && instance.input && instance.input.value !== newSearchTerm) {
          instance.isSyncing = true;
          instance.input.value = newSearchTerm;

          // Manually dispatch an 'input' event on the other instance.
          // This will trigger its *own* 'onChange' method, which does everything:
          // 1. Hides the 'X' button (via super.onChange)
          // 2. Restores its default content
          instance.input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        }
      });
      // --- END CORRECT SYNC LOGIC FOR RESET ---
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // If the input is empty, just open the dropdown.
      // Our CSS will handle showing the default content.
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.isScrolling) {
      this.isScrolling = false; // Reset the flag and do nothing
      return;
    }
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const searchType = this.currentSearchType;
    const queryKey = JSON.stringify([searchTerm.toLowerCase(), searchType]);
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;

    this.setLiveRegionLoadingState();
    this.open();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const typeParam = `&resources[type]=${searchType}`;
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search${typeParam}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (
          requestId !== this.requestId ||
          this.getQuery() !== searchTerm ||
          this.currentSearchType !== searchType
        ) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          if (instance.cachedResults) instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId) return;
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // --- NEW: RE-APPLY ACTIVE STATE TO BUTTONS ---
    // Since HTML was just replaced, we need to find the button matching our current type
    // and make it look active again.
    const activeBtn = this.querySelector(`.search-scope-btn[data-type="${this.currentSearchType}"]`);
    if (activeBtn) {
       // Remove active class from all (just in case)
       this.querySelectorAll('.search-scope-btn').forEach(btn => btn.classList.remove('active'));
       // Add to current
       activeBtn.classList.add('active');
    }
    // ----------------------------------------------

    // --- NEW: INJECT DEFAULT SUGGESTIONS ON "NO RESULTS" ---
    const suggestionsPlaceholder = this.predictiveSearchResults.querySelector('[data-no-results-suggestions-placeholder]');

    if (suggestionsPlaceholder && this.defaultSuggestionsHTML) {
      // We found the "no results" page, so inject the cached suggestions
      suggestionsPlaceholder.innerHTML = this.defaultSuggestionsHTML;

      // Optional: Change the heading text
      const heading = suggestionsPlaceholder.querySelector('.predictive-search__heading');
      if (heading) {
        heading.textContent = 'Check out some of these popular searches:';
        heading.style.marginTop = '2rem';
      }
    }
    // --- END NEW LOGIC ---

    // --- ADD THIS BLOCK TO INJECT PILLS ---
    const pillsPlaceholder = this.predictiveSearchResults.querySelector('[data-no-results-pills-placeholder]');
    if (pillsPlaceholder && this.defaultPillsHTML) {
      pillsPlaceholder.innerHTML = this.defaultPillsHTML;
    }
    // --- END ADDITION ---

    /*
    // --- NEW: BIND CLICK EVENTS FOR "NO RESULTS" PILLS ---
    this.predictiveSearchResults.querySelectorAll('.no-results__popular-pill').forEach((pill) => {
      pill.addEventListener('click', (event) => {
        event.preventDefault();
        const searchTerm = event.currentTarget.dataset.searchTerm;
        this.input.value = searchTerm; // Set the input value
        this.onChange(); // Re-run the search
      });
    });
    // --- END NEW LOGIC ---
    */

    // --- NEW: BIND CLICK EVENTS FOR "NO RESULTS" PILLS ---
    /*
    this.predictiveSearchResults.querySelectorAll('.no-results__popular-pill').forEach((pill) => {
      pill.addEventListener('click', (event) => {
        event.preventDefault();
        const searchTerm = event.currentTarget.dataset.searchTerm;
        this.input.value = searchTerm; // Set the input value
        this.onChange(); // Re-run the search
      });
    });
    */
    // --- END NEW LOGIC ---

    this.setLiveRegionResults();
    this.open();

    // --- START iOS KEYBOARD FIX ---
    this.predictiveSearchResults.addEventListener('scroll', () => {
      this.isScrolling = true;
      this.input.blur();
    }, { once: true });
    // --- END iOS KEYBOARD FIX ---
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const drawer = this.closest('#menu-drawer');
    const bottom = drawer ? Math.min(visibleBottom, drawer.getBoundingClientRect().bottom) : visibleBottom;
    this.resultsMaxHeight = Math.max(
      0,
      Math.floor(bottom - this.predictiveSearchResults.getBoundingClientRect().top - 8)
    );
    return this.resultsMaxHeight;
  }

  updateResultsMaxHeight() {
    if (!this.isOpen) return;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.updateResultsMaxHeight();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.abortController.abort();
    this.requestId++;
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.searchTerm = '';
      // Restore the default content
      this.predictiveSearchResults.innerHTML = this.defaultSearchResults;
    }
      
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

